import { apiClient } from '@boothiva/api-client';

export type EventStatus = 'Draft' | 'Published' | 'Archived';
export type EventExportStatus = 'Pending' | 'Processing' | 'Ready' | 'Failed';

export type EventExport = {
  id: string;
  eventId: string;
  status: EventExportStatus;
  downloadUrl: string | null;
  errorMessage: string | null;
  requestedAtUtc: string;
  completedAtUtc: string | null;
};

export type EventOverviewHour = {
  hourUtc: string;
  sessions: number;
  photosCaptured: number;
  printsSucceeded: number;
};

export type EventOverview = {
  devicesAssigned: number;
  templatesAssigned: number;
  sessions: number;
  photosCaptured: number;
  printsSucceeded: number;
  printsFailed: number;
  sessionsPrinted: number;
  sessionsWithoutPrint: number;
  printCapTotal: number | null;
  galleryPhotos: number;
  galleryPhotosHidden: number;
  firstSessionAtUtc: string | null;
  lastSessionAtUtc: string | null;
  timeline: EventOverviewHour[];
};

export type EventRecord = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  timeZoneId: string;
  isAllDay: boolean;
  venueName: string | null;
  venueAddressLine1: string | null;
  venueAddressLine2: string | null;
  venueCity: string | null;
  venuePostcode: string | null;
  venueCountry: string | null;
  venueNotes: string | null;
  venueLatitude: number | null;
  venueLongitude: number | null;
  clientContactName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  clientCompany: string | null;
  clientNotes: string | null;
  workflow: string;
  branding: string;
  privacy: string;
  printCapTotal: number | null;
  printCopiesMax: number;
  consentRequired: boolean;
  consentMessage: string | null;
  status: EventStatus;
  publishedVersionNumber: number | null;
  lastPublishedAtUtc: string | null;
  hasUnpublishedChanges: boolean;
  assignedDeviceIds: string[];
  createdAtUtc: string;
  updatedAtUtc: string | null;
};

export type EventVersionSummary = {
  versionNumber: number;
  snapshotHash: string;
  publishedByUserId: string;
  publishedAtUtc: string;
};

export type EventVersion = EventVersionSummary & {
  snapshot: unknown;
  assetManifest: unknown;
};

export type EventTemplateAssignment = {
  templateId: string;
  templateVersionNumber: number | null;
  displayOrder: number;
  isDefault: boolean;
};

export type AssignEventTemplate = Pick<EventTemplateAssignment, 'templateId' | 'templateVersionNumber' | 'isDefault'>;
export type AssignEventTemplatesRequest = { templates: AssignEventTemplate[] };
export type AssignEventDevicesRequest = { deviceIds: string[]; pins: (number | null)[] };

export type EventPage = { items: EventRecord[]; page: number; pageSize: number; totalCount: number; totalPages: number };
export type EventFilters = { page: number; pageSize: number; status?: string; sort?: string };

export type CreateEventRequest = {
  name: string;
  description: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  timeZoneId: string;
  isAllDay: boolean;
  venueName: string | null;
  venueAddressLine1: string | null;
  venueAddressLine2: string | null;
  venueCity: string | null;
  venuePostcode: string | null;
  venueCountry: string | null;
  venueNotes: string | null;
  venueLatitude: number | null;
  venueLongitude: number | null;
  clientContactName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  clientCompany: string | null;
  clientNotes: string | null;
  workflow: string;
  branding: string;
  privacy: string;
};

export type UpdateEventScheduleRequest = Pick<CreateEventRequest, 'name' | 'description' | 'startsAtUtc' | 'endsAtUtc' | 'timeZoneId' | 'isAllDay'>;
export type UpdateEventVenueRequest = { name: string | null; addressLine1: string | null; addressLine2: string | null; city: string | null; postcode: string | null; country: string | null; notes: string | null; latitude: number | null; longitude: number | null };
export type UpdateEventClientRequest = { contactName: string | null; email: string | null; phone: string | null; company: string | null; notes: string | null };
export type UpdateEventWorkflowRequest = { workflow: string };
export type UpdateEventPrintSettingsRequest = { printCapTotal: number | null; printCopiesMax: number };
export type UpdateEventPrivacySettingsRequest = { consentRequired: boolean; consentMessage: string | null };
export type UpdateEventBrandingRequest = { branding: string };
export type UpdateEventPrivacyRequest = { privacy: string };

export function listEvents(filters: EventFilters) {
  const query = new URLSearchParams({ page: String(filters.page), pageSize: String(filters.pageSize) });
  if (filters.status) query.set('status', filters.status);
  if (filters.sort) query.set('sort', filters.sort);
  return apiClient.get<EventPage>(`/api/v1/events?${query}`);
}

export const createEvent = (request: CreateEventRequest) => apiClient.post<EventRecord>('/api/v1/events', request);
export const getEventOverview = (eventId: string) => apiClient.get<EventOverview>(`/api/v1/events/${eventId}/overview`);
export const getEvent = (eventId: string) => apiClient.get<EventRecord>(`/api/v1/events/${eventId}`);
export const updateEventSchedule = (eventId: string, request: UpdateEventScheduleRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/schedule`, request);
export const updateEventVenue = (eventId: string, request: UpdateEventVenueRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/venue`, request);
export const updateEventClient = (eventId: string, request: UpdateEventClientRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/client`, request);
export const updateEventWorkflow = (eventId: string, request: UpdateEventWorkflowRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/workflow`, request);
export const updateEventPrintSettings = (eventId: string, request: UpdateEventPrintSettingsRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/print-settings`, request);
export const updateEventPrivacySettings = (eventId: string, request: UpdateEventPrivacySettingsRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/privacy-settings`, request);
export const updateEventBranding = (eventId: string, request: UpdateEventBrandingRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/branding`, request);
export const updateEventPrivacy = (eventId: string, request: UpdateEventPrivacyRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/privacy`, request);
export const deleteEvent = (eventId: string) => apiClient.delete<void>(`/api/v1/events/${eventId}`);
export const duplicateEvent = (eventId: string) => apiClient.post<EventRecord>(`/api/v1/events/${eventId}/duplicate`);
export const archiveEvent = (eventId: string) => apiClient.post<EventRecord>(`/api/v1/events/${eventId}/archive`);
export const publishEvent = (eventId: string) => apiClient.post<EventVersion>(`/api/v1/events/${eventId}/publish`);
export const listEventVersions = (eventId: string) => apiClient.get<EventVersionSummary[]>(`/api/v1/events/${eventId}/versions`);
export const getEventVersion = (eventId: string, versionNumber: number) => apiClient.get<EventVersion>(`/api/v1/events/${eventId}/versions/${versionNumber}`);
export const requestEventExport = (eventId: string) => apiClient.post<EventExport>(`/api/v1/events/${eventId}/export`);
export const getEventExport = (eventId: string, exportId: string) => apiClient.get<EventExport>(`/api/v1/events/${eventId}/exports/${exportId}`);
export const assignEventDevices = (eventId: string, request: AssignEventDevicesRequest) => apiClient.put<EventRecord>(`/api/v1/events/${eventId}/devices`, request);
export const listEventTemplates = (eventId: string) => apiClient.get<EventTemplateAssignment[]>(`/api/v1/events/${eventId}/templates`);
export const assignEventTemplates = (eventId: string, request: AssignEventTemplatesRequest) => apiClient.put<EventTemplateAssignment[]>(`/api/v1/events/${eventId}/templates`, request);
